/**
 * RecordReviewForm - Editable form for extracted record with per-field confidence indicators
 * Scientific data entry aesthetic with precision inputs
 */

import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { parseTypedAmount } from '@/lib/number';
import { FieldConfidenceIcon } from './ConfidenceIndicator';
import { Droplets, UtensilsCrossed, Activity, Anchor, Beaker } from 'lucide-react';

interface FieldConfig {
  name: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select';
  options?: string[];
  unit?: string;
  required?: boolean;
}

interface ExtractedRecord {
  id: string;
  record_type: string;
  extracted_data: Record<string, unknown>;
  corrected_data?: Record<string, unknown>;
  confidence_score: number;
  confidence_hints?: Record<string, { score?: number; hint?: string }>;
}

interface RecordReviewFormProps {
  record: ExtractedRecord;
  onChange: (data: Record<string, unknown>) => void;
  className?: string;
}

// Field configurations by record type
const fieldConfigs: Record<string, FieldConfig[]> = {
  water_reading: [
    { name: 'reading_date', label: 'Date', type: 'date', required: true },
    { name: 'reading_slot', label: 'Time Slot', type: 'select', options: ['5am', '7am', '3pm', '7pm', '10pm'], required: true },
    { name: 'ph', label: 'pH', type: 'number', unit: 'pH' },
    { name: 'temperature_c', label: 'Temperature', type: 'number', unit: '°C' },
    { name: 'dissolved_oxygen', label: 'Dissolved Oxygen', type: 'number', unit: 'ppm' },
    { name: 'ammonia_ppm', label: 'Ammonia', type: 'number', unit: 'ppm' },
    { name: 'nitrite_ppm', label: 'Nitrite', type: 'number', unit: 'ppm' },
    { name: 'alkalinity_ppm', label: 'Alkalinity', type: 'number', unit: 'ppm' },
    { name: 'salinity_ppt', label: 'Salinity', type: 'number', unit: 'ppt' },
    { name: 'transparency_cm', label: 'Transparency', type: 'number', unit: 'cm' },
    { name: 'depth_m', label: 'Depth', type: 'number', unit: 'm' },
    { name: 'water_color', label: 'Water Color', type: 'text' },
    { name: 'weather', label: 'Weather', type: 'text' },
  ],
  feeding: [
    { name: 'feeding_date', label: 'Date', type: 'date', required: true },
    { name: 'feeding_slot', label: 'Time Slot', type: 'select', options: ['7am', '10am', '11am', '1pm', '3pm', '4pm', '7pm'] },
    { name: 'feed_amount_kg', label: 'Amount', type: 'number', unit: 'kg', required: true },
    { name: 'feed_type', label: 'Feed Type', type: 'text' },
    { name: 'notes', label: 'Notes', type: 'text' },
  ],
  sampling: [
    { name: 'sampling_date', label: 'Date', type: 'date', required: true },
    { name: 'sample_count', label: 'Sample Count', type: 'number', required: true },
    { name: 'total_weight_g', label: 'Total Weight', type: 'number', unit: 'g', required: true },
    { name: 'gut_condition', label: 'Gut Condition', type: 'text' },
    { name: 'molt_stage', label: 'Molt Stage', type: 'text' },
    { name: 'health_notes', label: 'Health Notes', type: 'text' },
  ],
  harvest: [
    { name: 'harvest_date', label: 'Date', type: 'date', required: true },
    { name: 'harvest_type', label: 'Type', type: 'select', options: ['partial', 'final'], required: true },
    { name: 'total_weight_kg', label: 'Total Weight', type: 'number', unit: 'kg', required: true },
    { name: 'shrimp_count', label: 'Shrimp Count', type: 'number' },
    { name: 'price_per_kg', label: 'Price/kg', type: 'number', unit: '₱' },
    { name: 'buyer', label: 'Buyer', type: 'text' },
    { name: 'notes', label: 'Notes', type: 'text' },
  ],
  application: [
    { name: 'application_date', label: 'Date', type: 'date', required: true },
    { name: 'product_name', label: 'Product Name', type: 'text', required: true },
    { name: 'product_category', label: 'Category', type: 'select', options: ['probiotic', 'water_treatment', 'antibiotic', 'supplement', 'other'], required: true },
    { name: 'amount', label: 'Amount', type: 'number', required: true },
    { name: 'unit', label: 'Unit', type: 'text', required: true },
    { name: 'notes', label: 'Notes', type: 'text' },
  ],
};

const recordTypeIcons: Record<string, typeof Droplets> = {
  water_reading: Droplets,
  feeding: UtensilsCrossed,
  sampling: Activity,
  harvest: Anchor,
  application: Beaker,
};

const recordTypeLabels: Record<string, string> = {
  water_reading: 'Water Reading',
  feeding: 'Feeding',
  sampling: 'Sampling',
  harvest: 'Harvest',
  application: 'Application',
};

export function RecordReviewForm({ record, onChange, className }: RecordReviewFormProps) {
  const [formData, setFormData] = useState<Record<string, unknown>>(
    () => record.corrected_data || record.extracted_data || {}
  );

  // Handle field change
  const handleChange = (name: string, value: unknown) => {
    const newData = { ...formData, [name]: value };
    setFormData(newData);
    onChange(newData);
  };

  const fields = fieldConfigs[record.record_type] || [];
  const Icon = recordTypeIcons[record.record_type] || Activity;
  const typeLabel = recordTypeLabels[record.record_type] || record.record_type;

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-200">
          <Icon className="w-5 h-5 text-primary-600" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{typeLabel}</h3>
          <p className="text-xs text-gray-500 font-mono">{record.id.slice(0, 8)}</p>
        </div>
      </div>

      {/* Form fields */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          {fields.map(field => {
            const value = formData[field.name];
            const hints = record.confidence_hints?.[field.name];

            return (
              <FormField
                key={field.name}
                config={field}
                value={value}
                onChange={v => handleChange(field.name, v)}
                confidence={hints?.score}
                hint={hints?.hint}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * FormField - Individual form field with confidence indicator
 */
interface FormFieldProps {
  config: FieldConfig;
  value: unknown;
  onChange: (value: unknown) => void;
  confidence?: number;
  hint?: string;
}

function FormField({ config, value, onChange, confidence, hint }: FormFieldProps) {
  const { name, label, type, options, unit, required } = config;

  // Numbers are typed as text ("3,5" is 3.5; "abc" is refused with a message) and reported
  // as numbers. A record that changes underneath (re-extraction) replaces the text.
  const incoming = value !== undefined && value !== null && value !== '' ? Number(value) : null;
  const [numberText, setNumberText] = useState(() => (incoming !== null ? String(incoming) : ''));
  const numberParsed = parseTypedAmount(numberText);
  const typedNumber = 'value' in numberParsed ? numberParsed.value : null;
  const numberError = type === 'number' && 'error' in numberParsed ? numberParsed.error : undefined;
  useEffect(() => {
    if (type === 'number' && incoming !== typedNumber) setNumberText(incoming !== null ? String(incoming) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming]);

  const inputClasses = cn(
    'w-full px-3 py-2 rounded-lg text-sm',
    'border border-gray-200 bg-white',
    'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500',
    'placeholder:text-gray-400',
    'transition-colors',
    confidence !== undefined && confidence < 70 && 'border-amber-300 bg-amber-50/30'
  );

  const renderInput = () => {
    switch (type) {
      case 'select':
        return (
          <select
            value={String(value ?? '')}
            onChange={e => onChange(e.target.value)}
            className={inputClasses}
          >
            <option value="">Select...</option>
            {options?.map(opt => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );

      case 'date':
        return (
          <input
            type="date"
            value={String(value ?? '')}
            onChange={e => onChange(e.target.value)}
            className={inputClasses}
          />
        );

      case 'number':
        return (
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={numberText}
              onChange={e => {
                setNumberText(e.target.value);
                // A refused entry leaves the extracted value in the record and shows the
                // message; only a clean number (or a deliberate blank) replaces it.
                const parsed = parseTypedAmount(e.target.value);
                if ('value' in parsed) onChange(parsed.value);
              }}
              aria-invalid={numberError ? true : undefined}
              className={cn(inputClasses, unit && 'pr-12', numberError && 'border-red-300')}
              placeholder="—"
            />
            {unit && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-mono">
                {unit}
              </span>
            )}
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={String(value ?? '')}
            onChange={e => onChange(e.target.value)}
            className={inputClasses}
            placeholder="—"
          />
        );
    }
  };

  return (
    <div className={cn(
      'space-y-1',
      type === 'text' && name.includes('notes') && 'col-span-2'
    )}>
      <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
        <span>{label}</span>
        {required && <span className="text-red-500">*</span>}
        {confidence !== undefined && (
          <FieldConfidenceIcon score={confidence} hint={hint} />
        )}
      </label>
      {renderInput()}
      {numberError && (
        <p role="alert" className="text-xs text-red-600">
          {numberError}
        </p>
      )}
    </div>
  );
}
